import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import { useKeptVersion } from 'api/keptVersion';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CTALabel,
  ctaFieldStateFor,
  ctaKeyOf,
  ctaOptions,
} from 'data/adEditorCTA';
import { getLanguageCode } from 'utils/i18nHelper';
import tw from 'twin.macro';
import { Brand } from 'types/customer/types';
import ColorPicker from 'components/form/ColorPicker';
import Button from 'components/button/Button';
import useModalContext from 'hooks/context/modals-context';
import ResourceUpload from 'components/upload/ResourceUpload';
import { CustomerResource } from 'types/apiTypes';
import ApiDropdown from 'components/dropdown/api-dropdown/ApiDropdown';
import { useMutation } from 'hooks/sympl-mutation';
import { Image, X, TrashSimple } from '@phosphor-icons/react';
import { Title3 } from 'components/typography/Typography';
import MultiLocation from 'components/multi-location/MultiLocation';
import useApiList from 'hooks/useApiList';
import { TargetingLocation } from 'types/geolocationTypes';
import { normalizeTargetingRadius } from 'utils/geolocationHelpers';
import Dropdown, { DropdownItem } from 'components/dropdown/Dropdown';
import SaveStatus from 'components/save-status/SaveStatus';
import UndoRedo from 'components/undo-redo/UndoRedo';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import { brandPath, saveBrand, storableBrand } from 'api/brandAutoSave';
import { useBrandSavers } from 'views/settings/brands/BrandSavers';
import Input from 'components/form/input/Input';
import Label from 'components/form/Label';
import FacebookPageModal from 'components/modals/FacebookPageModal';
import { FileTypes } from 'types/fileTypes';
import useNavigationContext from 'hooks/context/nav-context';
import { ADD_CUSTOM_DOMAIN } from 'graphql/brands/mutations';
import { parseDomain, ParseResultType } from 'parse-domain';
import InformationToolTip from 'components/tooltip/InformationToolTip';
import ToolTip from 'components/tooltip/ToolTip';
import DnsStatus, { Validation } from 'views/settings/brands/DnsStatus';
import { fireEvent } from 'utils/eventHelper';
import { showIntercom } from 'utils/intercomHelper';
import { PostHogFeature } from 'posthog-js/react';
import Checkbox from 'components/checkbox/Checkbox';
import usePermissionContext from 'hooks/context/permission-context';
import { PERMISSIONS } from 'context/PermissionContext';
import { SUBSCRIPTION_CAPABILITIES } from 'types/subscriptions/types';

interface BrandEditFormProps {
  brand: Brand;
  handleDelete: () => void;
}

interface BrandEditFormState {
  name: string;
  default_primary_color?: string;
  default_secondary_color?: string;
  default_tertiary_color?: string;
  default_recruiter_id: number | null;
  default_logo?: CustomerResource | null;
  default_language_id: number;
  default_creatives?: CustomerResource[];
  default_locations?: TargetingLocation[];
  default_display_link?: string;
  default_banner_title?: string;
  default_cta?: string;
  default_facebook_page_id?: number;
  salary_mandatory?: boolean | null;
  custom_domain?: string;
  custom_domain_validations?: Validation[];
}

interface ConnectDomainPayload {
  brandId: number;
  input: { domain: string; sub_domain: string };
}

export const BrandEditForm: React.FC<BrandEditFormProps> = ({
  brand,
  handleDelete,
}) => {
  const brandFormData = {
    name: brand.name ?? '',
    default_primary_color: brand.default_primary_color ?? '#000000',
    default_secondary_color: brand.default_secondary_color ?? '#000000',
    default_tertiary_color: brand.default_tertiary_color ?? '#000000',
    default_recruiter_id: brand.default_recruiter?.id ?? null,
    default_logo: brand.default_logo ? brand.default_logo : null,
    default_language_id: brand.default_language_id ?? 141,
    default_creatives: brand.default_creatives ?? [],
    default_locations: (brand.default_locations ?? []).map(
      normalizeTargetingRadius
    ),
    default_display_link: brand.default_display_link ?? undefined,
    default_banner_title: brand.default_banner_title ?? undefined,
    // Dropped unless it is one of the three the API accepts. handleSubmit spreads formData
    // wholesale, so a legacy free-text value read from the brand would be sent straight back and
    // rejected -- failing the whole save, including fields the customer did change.
    default_cta: ctaKeyOf(brand.default_cta)
      ? brand.default_cta ?? undefined
      : undefined,
    default_facebook_page_id: brand.default_facebook_page_id ?? undefined,
    salary_mandatory: brand.salary_mandatory ?? false,
    custom_domain: brand.custom_domain ?? undefined,
  };

  // TODO: move to react-hook-forms + use unsaved.ts hook
  const [formData, _setFormData] = useState<BrandEditFormState>(brandFormData);
  /** Raised to draw the boxes again from the form's values (Undo). */
  const [boxesKey, setBoxesKey] = useState(0);

  const { pushModal, removeModal, modalList } = useModalContext();
  const { userHasPermission } = usePermissionContext();
  const { refetchBrands, hasSubscriptionCapability, isAdmin } =
    useNavigationContext();
  const [domainError, setDomainError] = useState(false);
  const [domainValidations, setDomainValidations] = useState<Validation[]>(
    brand.custom_domain_validations ?? []
  );

  const { data: languages } = useApiList('languages');
  const salaryMandatoryToggleEnabled = hasSubscriptionCapability(
    SUBSCRIPTION_CAPABILITIES.PAY_TRANSPARENCY_MANDATORY_TOGGLE
  );
  const canManageBrandCampaigns = userHasPermission(
    PERMISSIONS.MANAGE_CAMPAIGNS,
    'brand'
  );

  const brandHasCustomDomain = useMemo(() => {
    if (brand.custom_domain) return true;
    if (domainValidations && domainValidations.length) return true;
    return false;
  }, [brand, domainValidations]);

  const domainIsVerified = useMemo(
    () => !!(brand.custom_domain && domainValidations.length === 0),
    [brand]
  );

  const setFormData = (data: BrandEditFormState) => {
    _setFormData(data);
    brandSavers.touched(brand.id);
  };
  /*
   * A pop-up changes ONE thing of the form as it is now. It used to write
   * back the whole form as it stood when the pop-up was opened, which undid
   * whatever had changed since (a location that got its number meanwhile
   * was then made a second time; review of the slice).
   */
  const pick = (part: Partial<BrandEditFormState>) => {
    _setFormData((current) => ({ ...current, ...part }));
    brandSavers.touched(brand.id);
  };
  const popUpConfirmed = useRef(false);
  const atPopUpOpen = useRef<Pick<
    BrandEditFormState,
    'default_logo' | 'default_creatives' | 'default_locations'
  > | null>(null);
  const openPopUp = () => {
    popUpConfirmed.current = false;
    atPopUpOpen.current = {
      default_logo: formData.default_logo,
      default_creatives: formData.default_creatives,
      default_locations: formData.default_locations,
    };
  };

  const [connectDomain, { loading: domainLoading }] = useMutation<
    {
      addCustomDomain: {
        status: string;
        validations: Validation[];
      };
    },
    ConnectDomainPayload
  >(ADD_CUSTOM_DOMAIN);

  const getLanguage = (languageId: number) => {
    const selectedLanguage = languages.find(
      ({ key }) => key === languageId
    )?.label;
    return selectedLanguage;
  };

  // SYM-5856: the language the CTA options are DISPLAYED in on THIS screen. Taken from the form
  // rather than the saved brand so the labels follow a language the user has just changed but not
  // yet saved.
  const brandLanguage = getLanguageCode(formData.default_language_id ?? 141);

  // Reads as unset unless the stored value is one of the three. A brand may still hold a legacy
  // free-text CTA from before the field was constrained; showing that as a valid selection is what
  // let an unsaveable value look saved.
  const ctaField = ctaFieldStateFor(formData.default_cta, brandLanguage);

  const handleLanguageChange = (languageId: number) => {
    setFormData({ ...formData, default_language_id: languageId });
  };

  const handleLeadRecruiterChange = (userId: number) => {
    setFormData({ ...formData, default_recruiter_id: userId });
  };

  /*
   * Auto-save (scene 4, D32). Brand settings store themselves and never
   * wait for a publish. The custom domain is not part of it: it has its own
   * "connect" press, and the server does not take it from this store.
   */
  const brandSavers = useBrandSavers();
  /** The screen's work: everything but the custom domain. */
  const work = useMemo(() => {
    const rest = { ...formData };
    delete rest.custom_domain;
    return rest;
  }, [formData]);
  type BrandWork = typeof work;
  /** What the server holds: what was loaded, then what was last stored. */
  const storedWork = useRef<BrandWork>(work);
  /** Raised when `storedWork` changes, so that what follows is worked out again. */
  const [storedTick, setStoredTick] = useState(0);
  // Per-field rule (scene 19): a field that cannot be stored keeps its last
  // stored value, the others are stored, the corner names the one left out.
  //
  // Worked out only when the work or what is stored changed: handed to the
  // saver as a new thing at every draw, each draw looked like a new change,
  // which started the pause again and put the count of failed tries back to
  // zero, so a store that kept failing was tried every two seconds for ever
  // (review of the slice).
  const { storable, note } = useMemo(
    () => storableBrand(work, storedWork.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [work, storedTick]
  );
  /*
   * While the logo, pictures or locations pop-up is open, nothing is
   * stored: brand settings are in use at once, and a logo that is only
   * being tried out must not be the brand's logo for those seconds
   * (Steven, 5 Oct 2026). The store, and the Undo step, follow when the
   * pop-up closes: by "Confirm" or "Use this logo" with what was picked,
   * by "Cancel" with what was there before.
   */
  const popUpOpen = modalList.some(({ key }) =>
    /default-locations|resource-picker/.test(key)
  );
  /*
   * Only "Confirm" (or "Use this logo") keeps what was picked. Closed any
   * other way (Cancel, the X, Escape, a click outside) the pop-up leaves
   * the logo, pictures and locations as they were when it opened: closing
   * by the X used to count as a Confirm (review of the slice).
   */
  const closedUnconfirmed =
    !popUpOpen && atPopUpOpen.current !== null && !popUpConfirmed.current;
  useEffect(() => {
    if (popUpOpen) return;
    const back = atPopUpOpen.current;
    if (back && !popUpConfirmed.current) {
      _setFormData((current) => ({ ...current, ...back }));
    }
    atPopUpOpen.current = null;
  }, [popUpOpen]);
  const holding = popUpOpen || closedUnconfirmed;
  const beforePopUp = useRef({ storable, work });
  if (!holding) beforePopUp.current = { storable, work };
  const toStore = holding ? beforePopUp.current.storable : storable;

  /*
   * What the form showed when it was drawn, and what the brand really held
   * then. The form shows black for a colour that was never chosen, a
   * language and "no" for the salary rule where the brand has none. Sent as
   * shown, the first store of ANY field wrote those onto the brand: one
   * typed letter in the banner title made the brand's colours black. The
   * Save button did the same, but on a deliberate press (review of the
   * slice). So a field the person did not change goes back as it was.
   */
  const shownAtFirst = useRef(brandFormData);
  const heldAtFirst = useRef({
    default_primary_color: brand.default_primary_color ?? null,
    default_secondary_color: brand.default_secondary_color ?? null,
    default_tertiary_color: brand.default_tertiary_color ?? null,
    default_language_id: brand.default_language_id ?? null,
  });
  const salaryRuleWasSet = useRef(
    brand.salary_mandatory !== null && brand.salary_mandatory !== undefined
  );

  /** The body the Save button sent, with untouched fields as the brand held them. */
  const bodyOf = (value: BrandWork): Record<string, unknown> => {
    const body: Record<string, unknown> = {
      ...value,
      default_logo_id: value.default_logo?.id ?? null,
      default_creatives: (value.default_creatives ?? []).map(({ id }) => id),
      default_locations: value.default_locations?.map((location) => ({
        ...location,
        lon: location.lng,
      })),
    };
    (
      Object.keys(heldAtFirst.current) as (keyof typeof heldAtFirst.current)[]
    ).forEach((field) => {
      if (value[field] === shownAtFirst.current[field]) {
        body[field] = heldAtFirst.current[field];
      }
    });
    // Left out, the server leaves the salary rule as it is.
    if (
      !salaryRuleWasSet.current &&
      value.salary_mandatory === shownAtFirst.current.salary_mandatory
    ) {
      delete body.salary_mandatory;
    }
    return body;
  };

  // D10: the count of stores this brand's form builds on.
  const version = useKeptVersion('brand', brand.id);

  const storeBrand = useCallback(
    async (value: BrandWork, signal?: AbortSignal): Promise<string> => {
      const numbers = await saveBrand(brand.id, bodyOf(value), signal, {
        basedOn: version.basedOn(brand.id),
        stored: (count) => version.stored(brand.id, count),
      });
      /*
       * A location added on the screen has no number until it is stored.
       * The server hands the numbers back, in the order sent, and they are
       * written onto the locations: sent again without its number, a
       * location was made again at every later store (review of the slice).
       */
      const sent = value.default_locations ?? [];
      const numbered =
        numbers.length === sent.length
          ? sent.map((location, place) => ({
              ...location,
              id: location.id ?? numbers[place],
            }))
          : sent;
      storedWork.current = { ...value, default_locations: numbered };
      setStoredTick((n) => n + 1);
      if (numbers.length === sent.length && sent.some((one) => !one.id)) {
        _setFormData((current) => {
          const now = current.default_locations ?? [];
          // Only while the screen still shows the very locations that were sent.
          if (
            now.length !== sent.length ||
            now.some((one, place) => one.name !== sent[place].name)
          ) {
            return current;
          }
          return {
            ...current,
            default_locations: now.map((one, place) => ({
              ...one,
              id: one.id ?? numbers[place],
            })),
          };
        });
      }
      brandSavers.wasStored(brand.id);
      // The shared list of brands is NOT read again here: while it is being
      // read the forms on this screen are taken away and drawn afresh, which
      // dropped the corner's line and whatever was being typed (first
      // browser run of this screen). It is read again when the screen is
      // left: see BrandSavers.
      return new Date().toISOString();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand.id]
  );

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<BrandWork>({
    value: toStore,
    identity: `brand:${brand.id}`,
    save: storeBrand,
    flushOnHide: (value) =>
      sendKeepaliveSave(
        brandPath(brand.id),
        bodyOf(value),
        'POST',
        version.basedOn(brand.id)
      ),
  });

  const { canUndo, canRedo, undo, redo } = useUndoRedo<BrandWork>({
    // One step for a whole visit to a pop-up, not one per pick in it.
    value: holding ? beforePopUp.current.work : work,
    identity: `brand:${brand.id}`,
    // Ctrl/Cmd+Z belongs to the brand that was changed last, not to every
    // brand on the screen at once.
    keyboardWhen: () => brandSavers.isLastTouched(brand.id),
    apply: (earlier) => {
      _setFormData((current) => ({ ...current, ...earlier }));
      // Some boxes only start from the form's values: drawn again.
      setBoxesKey((n) => n + 1);
    },
  });

  // The brands screen speaks to the leave question for all its forms.
  useEffect(() => {
    brandSavers.report(brand.id, {
      failing: hasPending && hasFailed,
      saveNow,
      isKept,
      abandon,
    });
    return () => brandSavers.report(brand.id, null);
  }, [brandSavers, brand.id, hasPending, hasFailed, saveNow, isKept, abandon]);

  const hasSubdomain = (domain: string) => {
    const parsedDomain = parseDomain(domain);
    if (parsedDomain.type === ParseResultType.Listed) {
      const { subDomains } = parsedDomain;
      return subDomains;
    }
    return [];
  };

  const handleNewDomain = () => {
    const domainResult = hasSubdomain(formData.custom_domain ?? '');
    if (!domainResult.length) {
      setDomainError(true);
      setTimeout(() => {
        setDomainError(false);
      }, 3000);
      return;
    }

    connectDomain({
      variables: {
        brandId: brand.id,
        input: {
          domain: formData.custom_domain ?? '',
          sub_domain: domainResult.pop() ?? '',
        },
      },
    }).then((result) => {
      fireEvent('request_custom_subdomain', {
        brand: brand.id,
        domain: formData.custom_domain,
      });
      const data = result?.data?.addCustomDomain;
      setDomainValidations(data?.validations as Validation[]);
    });
  };

  return (
    <form
      tw="h-full bg-gray-50 p-8 relative rounded-b"
      key={boxesKey}
      onSubmit={(e) => e.preventDefault()}
    >
      {/*
        The arrows and the save line sit in the brand's own bar, and stay
        there while the brand scrolls: the bar is held at the top
        (BrandsSettings) and this row follows it, one bar's height (3rem)
        down. First in the form on purpose: a sticky row keeps its place from
        where it stands (Steven's hand test of 7 Oct 2026).
      */}
      <div tw="sticky top-12 z-20 h-0 -mt-8 mb-8" data-testid="brand-save-line">
        <div tw="absolute top-[-3.5rem] h-[2rem] right-20 flex mt-4 justify-end">
          <div tw="flex items-center gap-2">
            <UndoRedo
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
            />
            <OutOfDateBox
              refusal={refusal}
              onLoadTheirs={() => {
                abandon();
                window.setTimeout(() => window.location.reload(), 100);
              }}
              onKeepMine={() => {
                version.keepMine();
                retryRefused();
              }}
            />
            <SaveStatus
              state={saveState}
              keptAt={keptAt}
              note={note}
              // Before anything was stored in this visit there is no
              // "Saved at" for the note to follow: it stands alone.
              notSavedBecause={!keptAt ? note : undefined}
            />
          </div>
        </div>
      </div>
      <div tw="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full box-border">
        {/* Non-imagery */}
        <div tw="flex flex-col gap-8 bg-white shadow-md p-8 rounded-lg box-border">
          {/* brand name */}
          <div tw="flex flex-col gap-2">
            <div tw="flex flex-col">
              <Title3>Brand name</Title3>
            </div>
            <div tw="flex gap-4 flex-wrap gap-y-6">
              <div tw="flex flex-col justify-between w-full">
                <Input
                  id="brandName"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  tw="w-full"
                />
              </div>
            </div>
          </div>

          {/* Other data */}
          <div tw="flex flex-col gap-2">
            <div tw="flex flex-col">
              <Title3>Defaults</Title3>
            </div>

            <div tw="flex gap-4 flex-wrap gap-y-6">
              {/* lead recruiter */}
              <div tw="flex flex-col justify-between h-20">
                <Label htmlFor="leadRecruiter">Lead recruiter</Label>
                <ApiDropdown
                  type="recruiters"
                  mode="key"
                  testId="brand-default-recruiter"
                  placeholder={
                    (brand.default_recruiter &&
                      `${brand.default_recruiter?.firstname} ${brand.default_recruiter?.lastname}`) ??
                    'Select a lead recruiter'
                  }
                  onChange={(val) =>
                    handleLeadRecruiterChange(
                      (val as DropdownItem).key as number
                    )
                  }
                />
              </div>

              {/* Language (id) */}
              <div tw="flex flex-col justify-between h-20">
                <Label htmlFor="language">Campaign Language</Label>
                <ApiDropdown
                  mode="key"
                  value={formData.default_language_id}
                  type="languages"
                  testId="brand-default-language"
                  onChange={(val) =>
                    handleLanguageChange((val as DropdownItem).key as number)
                  }
                />
              </div>
            </div>

            {salaryMandatoryToggleEnabled &&
              (isAdmin || canManageBrandCampaigns) && (
                <div
                  tw="mt-4 flex flex-col gap-2"
                  data-testid="brand-salary-mandatory-control"
                >
                  <Title3>Salary input</Title3>
                  <Checkbox
                    key={String(formData.salary_mandatory)}
                    label="Require salary before publishing"
                    value={!!formData.salary_mandatory}
                    onChange={(checked) =>
                      setFormData({
                        ...formData,
                        salary_mandatory: checked,
                      })
                    }
                  />
                </div>
              )}
          </div>

          {/* Locations */}
          <div tw="flex flex-col gap-2">
            <Title3>Default targeting locations</Title3>
            <div
              className="location__container"
              tw="flex mb-2 gap-2 min-h-[2.5rem] flex-wrap"
            >
              {formData.default_locations?.map((location, index) => (
                <div
                  key={index}
                  className="group"
                  id="brand-location-items"
                  tw="flex gap-2 w-fit items-center bg-gray-100 rounded-md p-2 relative"
                >
                  <span tw="text-sm">{location.name}</span>
                  <span tw="text-sm text-gray-500">{location.radius} km</span>

                  <div
                    tw="absolute top-0 left-0 grid w-full h-full rounded-md bg-red-400 place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:cursor-pointer"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        default_locations: formData.default_locations?.filter(
                          (l) => l.id !== location.id
                        ),
                      });
                    }}
                  >
                    <TrashSimple
                      weight="bold"
                      tw="text-white cursor-pointer rounded-full"
                      size={20}
                    />
                  </div>
                </div>
              ))}

              <Button
                id="brand-locations"
                customStyle={tw`w-fit`}
                onClick={() => {
                  openPopUp();
                  pushModal({
                    show: true,
                    twclass: tw`box-content`,
                    children: (
                      <div tw="w-[70vw] h-fit">
                        <MultiLocation
                          language={
                            getLanguage(formData.default_language_id) ??
                            'English'
                          }
                          defaultValue={formData.default_locations}
                          onChange={(locations) =>
                            pick({ default_locations: locations })
                          }
                        />

                        <div tw="flex justify-end gap-2">
                          <Button
                            variant="default"
                            onClick={() => removeModal('default-locations')}
                          >
                            Cancel
                          </Button>
                          <Button
                            id="confirm-brand-locations"
                            onClick={() => {
                              popUpConfirmed.current = true;
                              removeModal('default-locations');
                            }}
                          >
                            Confirm
                          </Button>
                        </div>
                      </div>
                    ),
                    key: 'default-locations',
                  });
                }}
              >
                Add locations
              </Button>
            </div>
          </div>

          {/* Social Media defaults */}
          <div tw="flex flex-col gap-2">
            <Title3>Default social media settings</Title3>
            <div>
              <div tw="flex gap-2 items-center">
                <Label htmlFor="displayLink" fullWidth={false}>
                  Display link
                </Label>
                <InformationToolTip
                  name="custom-domain"
                  text="The display link should be a valid url."
                  icon="information"
                />
              </div>
              <Input
                id="displayLink"
                defaultValue={formData.default_display_link}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_display_link: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <Label htmlFor="bannerTitle">Banner title</Label>
              <Input
                id="bannerTitle"
                defaultValue={formData.default_banner_title}
                placeholder="e.g. Wanted"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_banner_title: e.target.value,
                  })
                }
              />
            </div>

            {/*
              SYM-5856. Every new campaign starts from this, and it is what J1 paints into the ad.
              Read in the BRAND's language, since that is the screen you are on; the campaign screen
              reads the same three in the VACANCY's language.

              What is SAVED is the canonical English value, because that is what the API validates
              and what becomes Meta's button type. Saving the translated label would publish a
              different button than the one chosen, so the key is mapped back through CTALabel here
              exactly as the ad builder does.
            */}
            <div>
              <Label htmlFor="defaultCta">Call to action</Label>
              <Dropdown
                testId="brand-default-cta"
                label={ctaField.label ?? 'Choose a call to action'}
                value={ctaField.value}
                items={ctaOptions(brandLanguage)}
                onChange={(value: number | string) =>
                  setFormData({
                    ...formData,
                    default_cta: CTALabel[value as keyof typeof CTALabel],
                  })
                }
              />
            </div>

            <PostHogFeature flag="facebook-page" match={true}>
              <div>
                <Label htmlFor="displayLink">Facebook page</Label>
                {/* Select the prefered facebook page */}
                <div tw="flex gap-4 items-center">
                  <ApiDropdown
                    type="facebook_pages"
                    mode="key"
                    placeholder="Use the sympl page"
                    value={formData.default_facebook_page_id}
                    onChange={(val) =>
                      setFormData({
                        ...formData,
                        default_facebook_page_id: (val as DropdownItem)
                          .key as number,
                      })
                    }
                    onClear={() => {
                      setFormData({
                        ...formData,
                        default_facebook_page_id: undefined,
                      });
                    }}
                  />

                  {/* Add Facebook  pages via modal */}
                  <FacebookPageModal
                    brandId={brand.id}
                    onComplete={refetchBrands} // TODO: check why this does not refresh the dropdown list
                  />
                </div>
              </div>
            </PostHogFeature>

            {/* Custom domain */}
            <div>
              <div tw="flex gap-2 items-center">
                <Label htmlFor="custom-domain" fullWidth={false}>
                  Your custom subdomain
                </Label>
                <InformationToolTip
                  name="custom-domain"
                  text="Linking a custom domain to your brand will allow you to use your own domain for your job listings."
                  icon="information"
                />
              </div>

              <PostHogFeature
                flag="custom-subdomain"
                match={true}
                fallback={
                  <div tw="flex gap-4 items-center">
                    <Button
                      id="brand-custom-domain-interest"
                      onClick={() => {
                        fireEvent('custom_domain_interested');
                        showIntercom();
                      }}
                    >
                      I'm interested 🙋‍♂️
                    </Button>
                  </div>
                }
              >
                <div tw="flex gap-4 items-center">
                  <ToolTip
                    text="A custom subdomain should be like example.sympl.be"
                    placement="top"
                    visible={domainError}
                    arrow
                  >
                    <Input
                      id="custom-domain"
                      defaultValue={formData.custom_domain}
                      placeholder="e.g. vacancies.example.com"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          custom_domain: e.target.value,
                        })
                      }
                      disabled={!!brandHasCustomDomain}
                    />
                  </ToolTip>

                  <Button
                    onClick={handleNewDomain}
                    loading={domainLoading}
                    disabled={!!brandHasCustomDomain}
                  >
                    Request connection
                  </Button>
                </div>
              </PostHogFeature>

              {!!brandHasCustomDomain && (
                <DnsStatus
                  status={domainIsVerified ? 'verified' : 'pending'}
                  validations={domainValidations}
                  label="Please add the followig records to your DNS settings:"
                />
              )}
            </div>
          </div>
        </div>

        {/* Imagery & name */}
        <div
          className="images_logos"
          tw="flex flex-col gap-8 bg-white shadow-md p-8 rounded-lg"
        >
          {/* Colors */}
          <div className="colors" tw="flex flex-col gap-2">
            <div tw="flex flex-col">
              <Title3>Default colors</Title3>
            </div>
            <div tw="flex gap-8 gap-y-6 flex-wrap">
              {/* primary color */}
              <div tw="flex flex-col justify-between">
                <Label htmlFor="primaryColor">Primary color</Label>
                <ColorPicker
                  id="primaryColor"
                  value={formData.default_primary_color}
                  ariaInvalid={false}
                  format="hex"
                  onChange={(color: string) =>
                    setFormData({ ...formData, default_primary_color: color })
                  }
                  align="left"
                  extraTop={true}
                />
              </div>
              {/* secondary color */}
              <div tw="flex flex-col justify-between">
                <Label htmlFor="secondaryColor">Secondary color</Label>
                <ColorPicker
                  id="secondaryColor"
                  value={formData.default_secondary_color}
                  ariaInvalid={false}
                  format="hex"
                  onChange={(color: string) =>
                    setFormData({ ...formData, default_secondary_color: color })
                  }
                  align="left"
                  extraTop={true}
                />
              </div>
              {/* tertiary color */}
              <div tw="flex flex-col justify-between">
                <Label htmlFor="tertiaryColor">Tertiary color</Label>
                <ColorPicker
                  id="tertiaryColor"
                  value={formData.default_tertiary_color}
                  ariaInvalid={false}
                  format="hex"
                  onChange={(color: string) =>
                    setFormData({ ...formData, default_tertiary_color: color })
                  }
                  align="left"
                  extraTop={true}
                />
              </div>
            </div>
          </div>

          {/* logo */}
          <div tw="flex flex-col justify-between">
            <Title3>Default logo</Title3>
            <div>
              {formData.default_logo ? (
                <div
                  className="group"
                  tw="relative w-fit min-h-[150px] flex flex-col justify-center shadow-md rounded-md"
                >
                  <img
                    src={formData.default_logo?.thumbnail_path}
                    alt="logo"
                    width={150}
                    height={150}
                    tw="rounded-md"
                  />
                  <X
                    tw="absolute top-2 right-2 text-2xl text-gray-500 cursor-pointer rounded-full bg-white hidden group-hover:block"
                    size={24}
                    onClick={() =>
                      setFormData({ ...formData, default_logo: null })
                    }
                  />
                </div>
              ) : (
                <div
                  id="brand-default-logo"
                  tw="border-2 w-[150px] h-[150px] grid place-items-center p-4 py-8 rounded-md cursor-pointer"
                  onClick={() => {
                    openPopUp();
                    pushModal({
                      show: true,
                      children: (
                        <div tw="w-[70vw] h-fit">
                          <ResourceUpload
                            fileTypes={[
                              FileTypes.JPEG,
                              FileTypes.JPG,
                              FileTypes.PNG,
                            ]}
                            maxFiles={1}
                            isLogo={true}
                            defaultValue={
                              formData.default_logo
                                ? [formData.default_logo]
                                : undefined
                            }
                            onChange={(value: CustomerResource[]) =>
                              pick({ default_logo: value[0] })
                            }
                          />

                          <div tw="flex justify-end gap-2">
                            <Button
                              variant="default"
                              onClick={() => removeModal('resource-picker')}
                            >
                              Cancel
                            </Button>
                            <Button
                              id="brand-default-logo-continue"
                              onClick={() => {
                                popUpConfirmed.current = true;
                                removeModal('resource-picker');
                              }}
                            >
                              Use this logo
                            </Button>
                          </div>
                        </div>
                      ),
                      key: 'resource-picker',
                    });
                  }}
                >
                  <Image tw="text-4xl text-gray-400" />
                  Upload a logo
                </div>
              )}
            </div>
          </div>

          {/* Images */}
          <div tw="flex flex-col justify-between">
            <Title3>Default images / videos</Title3>
            <div>
              <div tw="flex gap-4 flex-col md:flex-row ">
                <div tw="flex overflow-x-auto w-fit max-w-full flex-wrap shrink-0 gap-4 flex-col md:flex-row p-[3px] box-border">
                  {formData.default_creatives?.map((creative) => (
                    <div
                      key={creative.id}
                      className="group"
                      tw="relative flex flex-col justify-center shadow-md rounded-md w-36 h-36"
                    >
                      <img
                        src={creative.thumbnail_path}
                        alt="creative"
                        width={150}
                        height={150}
                        tw="rounded-md max-h-[150px]"
                      />
                      <X
                        tw="absolute top-2 right-2 text-2xl text-gray-500 cursor-pointer rounded-full bg-white hidden group-hover:block"
                        size={24}
                        onClick={() => {
                          setFormData({
                            ...formData,
                            default_creatives:
                              formData.default_creatives?.filter(
                                (c) => c.id !== creative.id
                              ),
                          });
                        }}
                      />
                    </div>
                  ))}
                  <div
                    id="brand-default-creatives"
                    tw="border-2 w-[150px] h-[150px] grid place-items-center p-4 py-8 rounded-md cursor-pointer text-center"
                    onClick={() => {
                      openPopUp();
                      pushModal({
                        show: true,
                        children: (
                          <div tw="w-[70vw] h-fit">
                            <ResourceUpload
                              fileTypes={[
                                FileTypes.JPEG,
                                FileTypes.JPG,
                                FileTypes.PNG,
                                FileTypes.MP4,
                              ]}
                              defaultValue={formData.default_creatives}
                              onChange={(value) =>
                                pick({ default_creatives: value })
                              }
                            />

                            <div tw="flex justify-end gap-2">
                              <Button
                                variant="default"
                                onClick={() => removeModal('resource-picker')}
                              >
                                Cancel
                              </Button>
                              <Button
                                id="brand-default-creatives-continue"
                                onClick={() => {
                                  popUpConfirmed.current = true;
                                  removeModal('resource-picker');
                                }}
                              >
                                Confirm
                              </Button>
                            </div>
                          </div>
                        ),
                        key: 'resource-picker',
                      });
                    }}
                  >
                    <Image tw="text-4xl text-gray-400 mb-1" />
                    Upload an image / video
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div tw="lg:col-span-2">
          <div tw="float-right">
            <Button variant="danger" onClick={handleDelete}>
              Delete brand
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
